import { debounce, wait } from "@/mono/lib/common";
import { gsap } from "@/mono/lib/gsap";
import { useCallback, useEffect, useMemo, useRef } from "react";
import useEvent from "./useEvent";

// HINT: Pass .current to ref to have it up to date in a hook
export function useCSSClassTransition({ element, className = "active" }) {
  const animateIn = useCallback(
    async (delay = 0) => {
      if (!element.current) return;

      await wait(delay);
      element.current?.classList.add(className);
    },
    [element, className]
  );

  const animateOut = useCallback(
    async (delay = 0) => {
      if (!element.current) return;

      await wait(delay);
      element.current?.classList.remove(className);

      return () => (element.current.style.display = "none");
    },
    [element, className]
  );

  return { animateIn, animateOut };
}

export function useStaggeredAnimation(
  { inParams = {}, outParams = {} } = {},
  itemAmount = 1
) {
  const tween = useRef();
  const _obj = useRef();

  const currStart = useRef(0);
  const currComplete = useRef(0);

  useEffect(() => {
    _obj.current = new Array(itemAmount).fill(0).map(() => ({ value: 0 }));
  }, [itemAmount]);

  if (!inParams.stagger || (outParams && !outParams.stagger)) {
    console.warn("No stagger specified for useStaggeredAnimation");
  }

  const animateIn = useMemo(
    (_params = {}) =>
      // DOC: https://dmitripavlutin.com/react-throttle-debounce/
      debounce((e) => {
        if (!inParams) return;

        const params = { ...inParams, ..._params };

        if (params.shouldCancel?.(e)) return;
        if (tween.current) tween.current?.kill();
        // if (_obj.current === 1) _obj.current = 0;

        delete params.event;
        delete params.shouldCancel;

        tween.current = gsap.to(_obj.current, {
          value: 1,
          ...params,

          onUpdate: () => {
            params?.onUpdate?.(_obj.current, { ...inParams });
          },

          stagger: {
            ...params.stagger,
            onStart: () => {
              params.stagger?.onStart?.(currStart.current, { ...inParams });
              currStart.current += 1;
            },
            onComplete: () => {
              params.stagger?.onComplete?.(currComplete.current, {
                ...inParams,
              });
              currComplete.current += 1;
            },
          },

          onComplete: () => {
            tween.current = null;

            params?.onComplete?.();
          },
        });
      }, inParams?.debounce || 0),
    [inParams]
  );

  const animateOut = useMemo(
    (_params = {}) =>
      debounce((e) => {
        if (!outParams) return;

        const params = { ...outParams, ..._params };

        if (tween.current) tween.current?.kill();
        if (params.shouldCancel?.(e)) return;
        // if (_obj.current === 0) _obj.current = 1;

        delete params.event;
        delete params.shouldCancel;

        tween.current = gsap.to(_obj.current, {
          current: 0,
          ...params,
          onUpdate: () => {
            params?.onUpdate?.(_obj.current);
          },
          onComplete: () => {
            tween.current = null;
            params?.onComplete?.();
          },
        });
      }, inParams?.debounce || 0),
    [outParams]
  );

  function kill() {
    tween.current?.kill?.();
  }

  return { animateIn, animateOut, kill };
}

/**
 * 
 * @param {*} params
 * @key event: @String
 * @key debounce: @Number 
 * @key ...gsap
 
 * @returns 
 */
export default function useAnimation({ inParams = {}, outParams = {} } = {}) {
  const tween = useRef();
  const _obj = useRef(0);

  const animateIn = useCallback(
    (_params = {}, data) => {
      if (!inParams) return;

      const params = { ...inParams, ..._params, ...data };

      if (params.shouldCancel?.(params)) return;
      if (tween.current) tween.current?.kill();
      if (_obj.current === 1) _obj.current = 0;

      delete params.event;
      delete params.shouldCancel;

      tween.current = gsap.to(_obj, {
        current: 1,
        ...params,
        onStart: () => {
          params?.onStart?.({ ...params });
        },
        onUpdate: () => {
          params?.onUpdate?.(_obj.current, { ...params });
        },
        onComplete: () => {
          tween.current = null;

          params?.onComplete?.({ ...params });
        },
      });
    },
    [inParams]
  );

  const animateOut = useCallback(
    (_params = {}, data) => {
      if (!outParams) return;

      const params = { ...outParams, ..._params, ...data };

      if (tween.current) tween.current?.kill();
      if (params.shouldCancel?.(params)) return;
      if (_obj.current === 0) _obj.current = 1;

      delete params.event;
      delete params.shouldCancel;

      tween.current = gsap.to(_obj, {
        current: 0,
        ...params,
        onUpdate: () => {
          params?.onUpdate?.(_obj.current, { ...params });
        },
        onComplete: () => {
          tween.current = null;
          params?.onComplete?.({ ...params });
        },
      });
    },
    [outParams]
  );

  function kill() {
    tween.current?.kill?.();
  }

  return { animateIn, animateOut, kill };
}

export function useToggleEventAnimation({ inParams, outParams }) {
  const { animateIn, animateOut, kill } = useAnimation({
    inParams,
    outParams,
  });

  useEvent(inParams?.event, (event) => animateIn(null, { data: event }));
  if (outParams)
    useEvent(outParams?.event, (event) => animateOut(null, { data: event }));

  return { animateIn, animateOut, kill };
}
